import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import Loading from '../../../shared/components/custom/Loading';
import useModal from '../../../shared/hooks/useModal';
import { formatDateTime } from '../../../util/date-and-time';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';

const REPLY_INDENT_PX = 20;
const p = partial('chat');
const s = partial('shared');
const senderLinkStyle = { fontSize: 12, fontWeight: 500 };

function getChatMessages(response) {
  if (Array.isArray(response)) {
    return response;
  }

  return (
    response?.messages ||
    response?.messageTree ||
    response?.chat?.messages ||
    response?.chat?.messageTree ||
    []
  );
}

function flattenMessages(messages, depth = 0) {
  const out = [];

  for (const message of messages || []) {
    out.push({ message, depth });

    if (message.replies?.length) {
      out.push(...flattenMessages(message.replies, depth + 1));
    }
  }

  return out;
}

function renderUserName(message) {
  const userId = message.userId || message.sender?.userId;
  const userName = message.userName || message.name || '-';

  if (!userId) {
    return <span style={senderLinkStyle}>{userName}</span>;
  }

  return (
    <Link to={`/users/edit/${userId}`} style={senderLinkStyle}>
      {userName}
    </Link>
  );
}

function renderCompanyEmployeeName(message) {
  const companyId =
    message.employeeDetails?.companyId || message.sender?.companyId;
  const companyName = message.employeeDetails?.companyName || '-';
  const userId = message.userId || message.sender?.userId;
  const userName =
    message.employeeDetails?.userName ||
    message.employeeDetails?.employeeName ||
    message.userName ||
    message.name ||
    '-';

  return (
    <>
      {companyId ? (
        <Link to={`/companies/edit/${companyId}?tab=1`} style={senderLinkStyle}>
          {companyName}
        </Link>
      ) : (
        <span style={senderLinkStyle}>{companyName}</span>
      )}{' '}
      -{' '}
      {userId ? (
        <Link to={`/users/edit/${userId}`} style={senderLinkStyle}>
          {userName}
        </Link>
      ) : (
        <span style={senderLinkStyle}>{userName}</span>
      )}
    </>
  );
}

function renderSenderName(message) {
  if (message.sender?.type === 'company_employee') {
    return renderCompanyEmployeeName(message);
  }

  return renderUserName(message);
}

const ChatMessagesList = ({ chatId }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [handleModal, visible, deleteID] = useModal();

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;

    setLoadingMessages(true);
    try {
      const chatWithMessages = await Api.chat.getAccessibleChat(chatId);
      setMessages(getChatMessages(chatWithMessages));
    } catch (err) {
      toast.error(err || p('errorLoadingMessages'));
    } finally {
      setLoadingMessages(false);
    }
  }, [chatId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const deleteMessage = async () => {
    try {
      await Api.chat.deleteMessage(deleteID);
      toast.success(p('messageDeleted'));
    } catch {
      toast.error(p('errorDeletingMessage'));
    } finally {
      handleModal();
      fetchMessages();
    }
  };

  const renderMessages = () => {
    return flattenMessages(messages).map(({ message, depth }, i) => {
      return (
        <tr
          style={{
            background: `rgba(0, 0, 0,${(depth * 3) / 100})`,
            borderBottom: '1px solid #eee',
          }}
          key={`chat-message-${message.id || i}`}
        >
          <td>
            <div
              style={{
                paddingLeft: depth * REPLY_INDENT_PX,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  display: 'block',
                  color: '#6B7280',
                  marginRight: 8,
                }}
              >
                {message.createdAt ? formatDateTime(message.createdAt) : '-'} -{' '}
                {renderSenderName(message)}
              </span>
              <span style={{ display: 'block' }}>{message.text}</span>
              {message.reactions?.length > 0 && (
                <span
                  style={{
                    padding: '4px 13px 4px 6px',
                    backgroundColor: '#eee',
                    borderRadius: 10,
                  }}
                >
                  {message.reactions.map((reaction, reactionIndex) => (
                    <span
                      key={`${message.id}-reaction-${reactionIndex}`}
                      title={reaction.userId || reaction.userName}
                      style={{ marginRight: -6 }}
                    >
                      {reaction.emoji}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </td>
          <td>
            <UncontrolledDropdown className="dashboard__table-more">
              <DropdownToggle>
                <DotsHorizontalIcon />
              </DropdownToggle>
              <DropdownMenu className="dropdown__menu">
                <DropdownItem
                  onClick={() => handleModal(message.id)}
                  className="danger"
                >
                  {s('delete')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </td>
        </tr>
      );
    });
  };

  return (
    <Container>
      <DeleteModal
        visible={visible}
        type={p('message')}
        handleModal={handleModal}
        modalAction={deleteMessage}
      />
      <Loading loading={loadingMessages} />
      <Row>
        <Col>
          <Table responsive striped>
            <tbody>
              {messages.length === 0 && !loadingMessages ? (
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      color: '#6B7280',
                      textAlign: 'center',
                      padding: 24,
                    }}
                  >
                    {p('noMessages')}
                  </td>
                </tr>
              ) : (
                renderMessages()
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default ChatMessagesList;
